import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, TextField, Typography } from '@mui/material';
import apiService from '../services/api';

type Status = { reseller_id: string; api_key_set: boolean; source: 'settings' | 'environment' | 'none'; updated_at: string | null };

const SOURCE_TEXT: Record<Status['source'], string> = {
  settings: 'Using the credentials saved here.',
  environment: "Using the credentials from the server's environment (.env). Save credentials here to replace them.",
  none: 'No credentials yet. Domain sync and the Domain Names report balance need them.',
};

/** Synergy Wholesale API credentials (Settings > Service Types). The API key is encrypted and never shown again. */
const SynergySettings: React.FC = () => {
  const [status, setStatus] = useState<Status | null>(null);
  const [resellerId, setResellerId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [busy, setBusy] = useState<'save' | 'test' | 'clear' | null>(null);
  const [message, setMessage] = useState<{ severity: 'success' | 'error' | 'info'; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const s = await apiService.getSynergySettings();
      setStatus(s);
      setResellerId(s.reseller_id || '');
    } catch {
      setMessage({ severity: 'error', text: 'Could not load the Synergy settings (admins only).' });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const apiError = (e: any, fallback: string) => {
    const detail = e?.response?.data?.detail;
    return typeof detail === 'string' ? detail : fallback;
  };

  const save = async () => {
    setBusy('save');
    setMessage(null);
    try {
      const s = await apiService.saveSynergySettings({ reseller_id: resellerId.trim(), api_key: apiKey.trim() || undefined });
      setStatus(s);
      setApiKey('');
      setMessage({ severity: 'success', text: 'Saved. The API key is encrypted and can\'t be viewed again.' });
    } catch (e) {
      setMessage({ severity: 'error', text: apiError(e, 'Could not save the credentials.') });
    } finally {
      setBusy(null);
    }
  };

  const test = async () => {
    setBusy('test');
    setMessage(null);
    try {
      const r = await apiService.testSynergySettings();
      setMessage(
        r.balance != null
          ? { severity: 'success', text: `Connected to Synergy Wholesale. Account balance: ${r.balance.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })}.` }
          : { severity: 'error', text: r.error || 'Could not get the balance.' }
      );
    } catch (e) {
      setMessage({ severity: 'error', text: apiError(e, 'Test failed.') });
    } finally {
      setBusy(null);
    }
  };

  const clear = async () => {
    if (!window.confirm('Remove the credentials saved here? Any credentials in the server environment will be used instead.')) return;
    setBusy('clear');
    setMessage(null);
    try {
      setStatus(await apiService.clearSynergySettings());
      setApiKey('');
      setResellerId('');
      setMessage({ severity: 'info', text: 'Saved credentials removed.' });
    } catch (e) {
      setMessage({ severity: 'error', text: apiError(e, 'Could not remove the credentials.') });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
      <Typography variant="h6" gutterBottom>Synergy Wholesale</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        API credentials for domain registry sync and the account balance on the Domain Names report. Find them in the
        Wholesale System under Your Account &gt; API Information. The API key is encrypted and never shown again.
      </Typography>
      {!status ? (
        <CircularProgress size={24} />
      ) : (
        <>
          <Alert severity={status.source === 'none' ? 'warning' : 'info'} sx={{ mb: 2 }}>{SOURCE_TEXT[status.source]}</Alert>
          {message && <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField
              label="API ID (reseller ID)"
              value={resellerId}
              onChange={(e) => setResellerId(e.target.value)}
              size="small"
              sx={{ minWidth: 240 }}
              autoComplete="off"
            />
            <TextField
              label="API key"
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              size="small"
              sx={{ minWidth: 300 }}
              autoComplete="new-password"
              placeholder={status.api_key_set ? '••••••••' : undefined}
              helperText={status.api_key_set ? 'A key is saved. Leave blank to keep it, or type a new one to replace it.' : undefined}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={save}
              disabled={busy !== null || !resellerId.trim() || (!apiKey.trim() && !status.api_key_set)}
            >
              {busy === 'save' ? <CircularProgress size={20} /> : 'Save'}
            </Button>
            <Button variant="outlined" onClick={test} disabled={busy !== null || status.source === 'none'}>
              {busy === 'test' ? <CircularProgress size={20} /> : 'Test connection'}
            </Button>
            {status.source === 'settings' && (
              <Button color="error" onClick={clear} disabled={busy !== null}>Remove saved credentials</Button>
            )}
          </Box>
        </>
      )}
    </Paper>
  );
};

export default SynergySettings;
